import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { toast } from "react-toastify";

import { useTranslation } from 'react-i18next';
import { Users } from 'src/Lib/Data/Users';
import useLocalStorage from 'src/Lib/DeviceMan/RKER_useLocalStorage';
import toCamelCase from 'src/Lib/Helper/Functions';
import RequestHelper from 'src/Lib/Helper/RequestHelper';
import { StaticsData } from 'src/Lib/Data/Statics';

const AuthContext = createContext(undefined);

export function useAuthentication() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [authShow, setAuthShow] = useState(false);
    const [storage, setStorage] = useLocalStorage('hybrid'); // { token, hybrid }
    const { i18n } = useTranslation();
    const panel = 'NPAN_TSESSION';

    // Derived state from storage (single source of truth)
    const token = storage?.token ?? null;
    const hybrid = storage?.hybrid ?? null;
    const isLogin = Boolean(token && hybrid);

    // Pure derivation for direction (used for className)
    const direction = useMemo(() => {
        const lang = hybrid?.language;
        return StaticsData.Languages.find((x) => x.id === lang)?.direction || 'ltr';
    }, [hybrid?.language]);

    // Apply global side-effects on language/theme changes
    useEffect(() => {
        const lang = hybrid?.language;
        if (lang) {
            document.documentElement.setAttribute('lang', lang);
            document.documentElement.setAttribute('dir', direction);
            i18n.changeLanguage(lang).catch(() => {});
        }

        if (hybrid?.theme) {
            document.documentElement.setAttribute('theme', hybrid.theme);
            const theme = toCamelCase(hybrid.theme);
            // Load corresponding theme stylesheet
            import(`src/Themes/${theme}.scss`);
        }
    }, [hybrid?.language, hybrid?.theme, direction, i18n]);

    // Fetch user profile when a token exists
    useEffect(() => {
        let cancelled = false;

        const fetchUser = async () => {
            if (!token) return;

            try {
                const res = await RequestHelper.get(panel, '/user');

                if (cancelled) return;

                if (res?.success && res?.object) {
                    setStorage({
                        token,
                        hybrid: res.object.hybrid,
                    });


                    if (
                        res.object?.version &&
                        res.object.version !== process.env.REACT_APP_VERSION
                    ) {
                        toast.info(
                            <div>
                                <span>There is a new version available.</span>
                                <button
                                    onClick={() => {
                                        window.location.replace(
                                            window.location.pathname + "?v=" + Date.now()
                                        );
                                    }}
                                    style={{
                                        marginTop: "10px",
                                        background: "#ff0000",
                                        color: "#fff",
                                        border: "none",
                                        padding: "4px 8px",
                                        cursor: "pointer",
                                        borderRadius: "4px"
                                    }}
                                >
                                    Reload
                                </button>
                            </div>,
                            {
                                autoClose: false,
                                closeOnClick: false,
                                toastId: "app-version-outdated"
                            }
                        );
                    }

                } else {
                    setStorage(null);
                }
            } catch (e) {
                if (!cancelled) {
                    // Keep current storage, only log the error
                    console.error('Error fetching user', e);
                }
            }
        };

        fetchUser();
        return () => {
            cancelled = true;
        };
    }, [token, panel, setStorage]);

    // Update hybrid profile parts; persists into localStorage
    const updateHybrid = useCallback(
        async (changes) => {
            if (!hybrid) return;

            if (Object.prototype.hasOwnProperty.call(changes, 'language')) {
                try {
                    await i18n.changeLanguage(changes.language);
                } catch {
                    // ignore i18n change errors
                }
            }

            setStorage((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    hybrid: {
                        ...prev.hybrid,
                        ...changes,
                    },
                };
            });
        },
        [hybrid, i18n, setStorage]
    );

    // Dummy sign-in for local/dev usage
    const signInDummy = useCallback(
        async (username, password) => {
            const user = Users.find(
                (u) => u.username === username && u.password === password
            );

            if (user) {
                setStorage({
                    token: 'SampleText',
                    hybrid: user,
                });
                return true;
            }

            return false;
        },
        [setStorage]
    );

    // Real sign-in
    const signIn = useCallback(
        async (username, password) => {
            try {
                const res = await RequestHelper.post(panel, '/login', {
                    username,
                    password,
                });

                if (res?.success) {
                    setStorage(res.object);
                    setAuthShow(false);
                    return res.object;
                }

                toast.error(res?.message || 'Login failed');
                return false;
            } catch {
                toast.error('Login failed');
                return false;
            }
        },
        [panel, setStorage]
    );

    // Sign out
    const signOut = useCallback(async () => {
        try {
            const res = await RequestHelper.post(panel, '/logout', {});
            if (res?.success) {
                setStorage(null);
            } else {
                toast.error(res?.message || 'Logout failed');
            }
        } catch {
            toast.error('Logout failed');
        }
    }, [panel, setStorage]);

    const value = useMemo(
        () => ({
            hybrid,
            token,
            isLogin,
            signIn,
            updateHybrid,
            signOut,
            signInDummy,
            authShow,
            setAuthShow,
        }),
        [hybrid, token, isLogin, signIn, updateHybrid, signOut, signInDummy, authShow]
    );

    return (
        <AuthContext.Provider value={value}>
            <div
                className={`lang-${hybrid?.language} direction-${direction} theme-${hybrid?.theme}`}
            >
                {children}
            </div>
        </AuthContext.Provider>
    );
}